import React from 'react';

import { Camera } from '@yandex-360/iconostasis/dist/icons/Camera';
import { CameraBlocked } from '@yandex-360/iconostasis/dist/icons/CameraBlocked';
import { CameraOff } from '@yandex-360/iconostasis/dist/icons/CameraOff';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../../../../init-store';
import { SvgImage } from '../../../../../../shared/components';
import { selectShouldShowDeviceErrorModal } from '../../../../../../shared/features/device-manager/selectors';
import { isMobileUa } from '../../../../../../shared/features/uatraits';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { MediaStatus } from '../../../../constants';
import { ToolbarButton, Props as ToolbarButtonProps } from '../toolbar-button';
import { ToolbarOrbTooltip } from '../toolbar-orb-tooltip';
import css from './style.css';
import { CameraButtonProps } from './types';

const shouldShowSelector = (state: RootState) => selectShouldShowDeviceErrorModal(state, 'videoinput');

export const CameraButton: React.FC<CameraButtonProps> = ({ onToggleMute, status = MediaStatus.UNMUTED, ...rest }) => {
    const shouldShowError = useSelector(shouldShowSelector);
    const isMobile = useSelector(isMobileUa);

    const isBadgeErrorVisible = shouldShowError && status !== MediaStatus.BLOCKED;

    const specificProps: Pick<ToolbarButtonProps, 'iconName' | 'icon' | 'title' | 'onClick'> = (() => {
        switch (status) {
            case MediaStatus.BLOCKED:
                return {
                    icon: <CameraBlocked variant="solid" size="md" />,
                    title: i18n('toolbar', 'organizer-revoked-camera-permission'),
                    testId: 'organizer-revoked-camera-permission-button',
                };
            case MediaStatus.UNMUTED:
                return {
                    icon: <Camera variant="solid" size="md" />,
                    title: i18n('toolbar', 'turn-off-camera'),
                    testId: 'turn-off-camera-button',
                    onClick: onToggleMute,
                };
            case MediaStatus.MUTED:
                return {
                    icon: <CameraOff variant="solid" size="md" />,
                    title: i18n('toolbar', 'turn-on-camera'),
                    testId: 'turn-on-camera-button',
                    onClick: onToggleMute,
                };
        }
    })();

    const buttonProps = {
        ...rest,
        ...specificProps,
    };

    let toolbarButton = <ToolbarButton {...buttonProps} />;

    if (status === MediaStatus.BLOCKED && !isMobile) {
        toolbarButton = (
            <ToolbarOrbTooltip content={i18n('toolbar', 'organizer-revoked-camera-permission')}>
                <div>{toolbarButton}</div>
            </ToolbarOrbTooltip>
        );
    }

    return (
        <div className={css.CameraButton}>
            {toolbarButton}
            {isBadgeErrorVisible ? <SvgImage className={css.BadgeError} noContainer imageName="badgeError" /> : null}
        </div>
    );
};
